import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import coverImg from "./../../images/blog/shadow-ai-at-work.svg";
import pathsImg from "./../../images/blog/shadow-ai-at-work-paths.svg";
import FAQ from "../../components/FAQs";
import { DEMO_LINK } from "../../components/demoBooking";
import CopyLinkButton from "../../components/CopyLinkButton";

/**
 * Shadow AI at Work: What Your Team Is Already Automating Without You.
 * Original, SEO-focused content with verifiable, currently-live references.
 */

export default function Blog_ShadowAI() {
  const title =
    "Shadow AI at Work: What Your Team Is Already Automating Without You";
  const description =
    "Two-thirds of office professionals use AI at work against policy. What shadow AI really costs, why blocking backfires, and how to govern it in five steps.";
  const canonicalUrl = "https://bossdrop.ai/blog/shadow-ai-at-work";
  const published = "2026-09-10";
  const readTime = "12 min read";

  // SEO keyword cluster (base BossDrop terms + shadow AI governance intent)
  const keywords = [
    "bossdrop",
    "bossdrop ai",
    "bossdrop.ai",
    "bossdrop company",
    "shadow ai",
    "shadow ai risks",
    "shadow ai governance",
    "shadow ai policy",
    "unapproved ai tools at work",
    "unsanctioned ai tools",
    "employees using ai without approval",
    "byoai",
    "bring your own ai",
    "ai usage policy",
    "ai governance policy",
    "ai governance framework",
    "enterprise ai governance",
    "ai acceptable use policy",
    "how to detect shadow ai",
    "how to stop shadow ai",
    "is shadow ai a security risk",
    "ai data leakage",
    "ai audit trail",
    "least privilege ai agents",
    "human in the loop approvals",
    "ai project management",
    "agentic automation",
    "ai workflow automation",
    "ai agent automation",
    "work management",
    "coordination overhead",
    "autonomous work management",
  ];

  const shareUrl = encodeURIComponent(canonicalUrl);
  const shareText = encodeURIComponent(title);
  const onShare = (net) => {
    const map = {
      x: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
    };
    window.open(map[net], "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    document.title = title;
    setCanonical(canonicalUrl);
    setMeta(description);
    setKeywords(keywords.join(", "));
    setOG("og:type", "article");
    setOG("og:title", title);
    setOG("og:description", description);
    setOG("og:url", canonicalUrl);
    setOG("og:image", toAbs(coverImg));
    setOG("twitter:card", "summary_large_image");
    setOG("twitter:title", title);
    setOG("twitter:description", description);
    setOG("twitter:image", toAbs(coverImg));

    injectLDJSON("ldjson-blogpost", {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: title,
      description,
      datePublished: published,
      dateModified: published,
      author: { "@type": "Organization", name: "BossDrop" },
      publisher: {
        "@type": "Organization",
        name: "BossDrop",
        logo: { "@type": "ImageObject", url: "https://bossdrop.ai/icon.png" },
      },
      image: toAbs(coverImg),
      mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      keywords: keywords.join(", "),
      articleSection: "AI Governance",
    });

    injectLDJSON("ldjson-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is shadow AI?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Shadow AI is any use of AI tools, models, or agents for work that happens outside your organization's approval, visibility, and controls. It is the AI-era version of shadow IT: employees adopt a tool on their own because it solves a real problem faster than the sanctioned path does.",
          },
        },
        {
          "@type": "Question",
          name: "How common is shadow AI in the workplace?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "PagerDuty's 2026 Shadow AI Survey of 1,250 office professionals at companies with $500 million or more in revenue found that 66% had used AI at work despite believing it was not permitted under company policy, rising to 72% at organizations with 1,500 or more employees. UpGuard's State of Shadow AI report found roughly 8 in 10 employees using unauthorized AI tools, and IBM's 2025 Cost of a Data Breach Report found 1 in 5 organizations reported a breach tied to shadow AI.",
          },
        },
        {
          "@type": "Question",
          name: "Why do employees use unapproved AI tools?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Mostly because the sanctioned path is slower than the unsanctioned one. Microsoft and LinkedIn's Work Trend Index found 78% of AI users bring their own AI tools to work, and that only 39% of people using AI at work had received AI training from their employer. PagerDuty found 89% of workers first encountered their AI tool in their personal life. When people already know a tool works and no approved equivalent exists, they use it.",
          },
        },
        {
          "@type": "Question",
          name: "Does blocking AI tools stop shadow AI?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Rarely. UpGuard found that 41% of employees work around blocked applications, and that employees who received AI safety training were among the most frequent users of unapproved tools. Blocking moves the activity to personal devices and personal accounts, where you have no visibility at all. Providing a faster governed path works better than restricting the ungoverned one.",
          },
        },
        {
          "@type": "Question",
          name: "How do you detect shadow AI in your organization?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Start with an amnesty inventory rather than a monitoring sweep: ask teams what they already use, with no penalty attached. Pair it with network and SaaS discovery data, expense reports for personal AI subscriptions, and regular audits. IBM found that among organizations that do have an AI governance policy, only 34% audit for unsanctioned AI, which is why most inventories are stale.",
          },
        },
        {
          "@type": "Question",
          name: "What does an AI governance policy need to include?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "At minimum: a data classification rule saying what may and may not be sent to which class of tool, a short list of approved tools with a fast path to add more, scoped least-privilege access for anything that can act, an approval gate on irreversible actions, and an audit trail that records what ran, on what data, and who approved it. A policy that only lists prohibitions produces shadow AI rather than preventing it.",
          },
        },
      ],
    });

    // reveal on scroll
    const els = Array.from(document.querySelectorAll(".reveal"));
    if (!("IntersectionObserver" in window) || !els.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="page container">
      {/* Breadcrumbs */}
      <nav
        className="breadcrumbs reveal"
        style={{ "--delay": "0ms" }}
        aria-label="Breadcrumb"
      >
        <Link to="/">Home</Link>
        <span>›</span>
        <Link to="/blog">Blog</Link>
        <span>›</span>
        <span aria-current="page">Shadow AI</span>
      </nav>

      {/* Feature hero */}
      <div className="hero-feature reveal" style={{ "--delay": "40ms" }}>
        <div className="hero-gradient" />
        <div className="hero-grid">
          <div className="hero-card">
            <img
              src={coverImg}
              alt="Shadow AI at work: a small amount of sanctioned AI use visible above the line, and a much larger volume of unapproved AI tools running below it"
              width="1200"
              height="630"
            />
          </div>

          <div className="hero-text">
            <div className="pill">AI GOVERNANCE</div>
            <h1 className="h1">{title}</h1>

            <div className="byline">
              <div className="author">
                <div className="avatar" aria-hidden="true">
                  BD
                </div>
                <div>
                  <div className="author-name">BossDrop Team</div>
                  <div className="meta">
                    {new Date(published).toLocaleDateString()} • {readTime}
                  </div>
                </div>
              </div>

              <div className="share">
                <button
                  aria-label="Share on X"
                  onClick={() => onShare("x")}
                  title="Share on X"
                >
                  {iconX()}
                </button>
                <button
                  aria-label="Share on LinkedIn"
                  onClick={() => onShare("linkedin")}
                  title="Share on LinkedIn"
                >
                  {iconLinkedIn()}
                </button>
                <button
                  aria-label="Share on Facebook"
                  onClick={() => onShare("facebook")}
                  title="Share on Facebook"
                >
                  {iconFacebook()}
                </button>
                <CopyLinkButton url={canonicalUrl} />
              </div>
            </div>
          </div>
        </div>

        {/* CTA bar */}
        <div className="hero-cta">
          <span>
            Shadow AI is <strong>demand</strong> showing up in the wrong place.
            BossDrop gives it a <strong>governed path</strong> instead of a
            hiding place.
          </span>
          <a
            className="btn btnDemo"
            {...DEMO_LINK}
            aria-label="Book a demo"
          >
            Book a demo
          </a>
        </div>
      </div>

      {/* ---- Article ---- */}
      <article className="article">
        <p className="lede reveal" style={{ "--delay": "80ms" }}>
          Somewhere in your company right now, someone is pasting a customer
          list into a chatbot they pay for personally. They are not being
          reckless. They have a deadline, the approved tooling does not do the
          thing, and the unapproved tool does it in ninety seconds.
          <br />
          <br />
          That is <strong>shadow AI</strong>: work getting automated outside
          your visibility, approvals, and controls. It is not a fringe behavior
          and it is not confined to junior staff. It is the majority behavior,
          it is most common among senior people, and it is now measurably
          expensive. This post covers what the research actually says, why the
          obvious fix makes it worse, and a five-step framework for converting
          shadow AI into governed capability.
        </p>

        <nav
          className="toc reveal"
          style={{ "--delay": "100ms" }}
          aria-label="Table of contents"
        >
          <a href="#what-is-shadow-ai">What is shadow AI?</a>
          <a href="#how-common">How common is shadow AI at work?</a>
          <a href="#why">Why employees use unapproved AI tools</a>
          <a href="#cost">What shadow AI actually costs</a>
          <a href="#blocking">Why blocking AI tools does not work</a>
          <a href="#two-paths">Two paths for the same request</a>
          <a href="#framework">A five-step framework to bring it into the light</a>
          <a href="#bossdrop">What this looks like in BossDrop</a>
          <a href="#metrics">Metrics that tell you it is working</a>
          <a href="#resources">External resources</a>
          <a href="#faq">FAQ</a>
        </nav>

        <h2 id="what-is-shadow-ai" className="reveal" style={{ "--delay": "120ms" }}>
          What is shadow AI?
        </h2>
        <p className="reveal" style={{ "--delay": "140ms" }}>
          <strong>Shadow AI</strong> is any use of AI tools, models, or agents
          for company work that sits outside your approval, visibility, and
          controls. It is the AI-era descendant of shadow IT, with one
          important difference. Shadow IT usually meant an unapproved place to{" "}
          <em>store</em> things. Shadow AI means an unapproved place to{" "}
          <em>send</em> things, and increasingly an unapproved thing that{" "}
          <em>acts</em> on your behalf.
        </p>
        <p className="reveal" style={{ "--delay": "150ms" }}>
          It shows up in three shapes, and most organizations only look for the
          first:
        </p>
        <ul className="reveal checks" style={{ "--delay": "160ms" }}>
          <li>
            ✅ <strong>Shadow input.</strong> Company data pasted into a
            personal AI account: emails, meeting notes, customer records,
            pricing, roadmaps.
          </li>
          <li>
            ✅ <strong>Shadow output.</strong> AI-generated analysis, code, or
            copy that enters a decision with no record of how it was produced or
            what it was based on.
          </li>
          <li>
            ✅ <strong>Shadow action.</strong> Personal API keys, browser
            extensions, and side-account agents that read from or write to real
            systems. This is the fastest-growing shape and the least monitored.
          </li>
        </ul>

        <h2 id="how-common" className="reveal" style={{ "--delay": "180ms" }}>
          How common is shadow AI at work?
        </h2>
        <p className="reveal" style={{ "--delay": "200ms" }}>
          Common enough that "does this happen here" is the wrong question. The
          right question is what share of it you can see.
        </p>
        <p className="reveal" style={{ "--delay": "210ms" }}>
          PagerDuty's{" "}
          <a
            href="https://www.pagerduty.com/newsroom/shadow-ai-workplace-survey-2026/"
            target="_blank"
            rel="noopener noreferrer"
          >
            2026 Shadow AI Survey of 1,250 office professionals
          </a>{" "}
          at companies with $500 million or more in revenue found that 66% had
          used AI at work even though they believed it was not permitted under
          company policy, rising to 72% at organizations with 1,500 or more
          employees. In the same survey, 88% had shared work-related
          information with public AI tools, including 34% who entered customer
          data and 31% who shared financial information or confidential company
          documents and strategies.
        </p>
        <p className="reveal" style={{ "--delay": "220ms" }}>
          UpGuard's{" "}
          <a
            href="https://www.upguard.com/press/new-research-from-upguard-reveals-68-of-security-leaders-admit-to-unauthorized-ai-usage"
            target="_blank"
            rel="noopener noreferrer"
          >
            State of Shadow AI research
          </a>{" "}
          puts the figure at roughly 8 in 10 employees using unauthorized AI
          tools, and found that seniority makes it more likely, not less:
          senior leadership is around 50% more likely to use shadow AI, and 68%
          of security leaders including CISOs admitted to building unauthorized
          AI into their daily workflows. PagerDuty found a matching perception
          gap: 81% of respondents believed leadership operates under a different
          set of AI rules than everyone else.
        </p>

        <div className="callout info reveal" style={{ "--delay": "230ms" }}>
          <strong>The uncomfortable read:</strong> if your executives are the
          heaviest shadow AI users, a policy aimed at frontline discipline is
          aimed at the wrong floor of the building.
        </div>

        <h2 id="why" className="reveal" style={{ "--delay": "240ms" }}>
          Why employees use unapproved AI tools
        </h2>
        <p className="reveal" style={{ "--delay": "250ms" }}>
          Almost never to cause harm. The pattern in the research is
          consistently about speed, familiarity, and an absent alternative.
        </p>
        <p className="reveal" style={{ "--delay": "260ms" }}>
          Microsoft and LinkedIn's{" "}
          <a
            href="https://www.microsoft.com/en-us/worklab/work-trend-index/ai-at-work-is-here-now-comes-the-hard-part"
            target="_blank"
            rel="noopener noreferrer"
          >
            Work Trend Index survey of 31,000 knowledge workers
          </a>{" "}
          found 78% of AI users bring their own AI tools to work, and that only
          39% of people using AI at work had received any AI training from their
          company. It also found 52% were reluctant to admit using AI on their
          most important tasks. PagerDuty adds the origin story: 89% of workers
          who use AI for work first encountered the tool in their personal life,
          and 79% now use it more at work than at home.
        </p>
        <p className="reveal" style={{ "--delay": "270ms" }}>
          Put those together and the mechanism is obvious. People arrive already
          fluent in a tool. The company offers no equivalent, no training, and a
          policy written in the language of prohibition. PagerDuty found 77% of
          respondents believe their employer's AI restrictions are limiting
          their professional growth, and 75% would be likely to leave for a job
          with better AI skills development. Shadow AI is what demand does when
          the sanctioned path is slower than the unsanctioned one.
        </p>

        <div className="flow reveal" style={{ "--delay": "280ms" }}>
          <div className="step">
            <div className="badge">1</div>
            <div>
              <strong>A real deadline creates real pressure.</strong> The work
              is due, and the coordination around it is the slow part.
            </div>
          </div>
          <div className="step">
            <div className="badge">2</div>
            <div>
              <strong>The sanctioned path has friction.</strong> A request
              queue, a training gap, or simply no approved tool that does the
              job.
            </div>
          </div>
          <div className="step">
            <div className="badge">3</div>
            <div>
              <strong>A known personal tool is one tab away.</strong> No
              procurement, no ticket, no waiting.
            </div>
          </div>
          <div className="step">
            <div className="badge">4</div>
            <div>
              <strong>It works, so it becomes the process.</strong> Quietly,
              undocumented, and dependent on one person staying in the role.
            </div>
          </div>
        </div>

        <h2 id="cost" className="reveal" style={{ "--delay": "300ms" }}>
          What shadow AI actually costs
        </h2>
        <p className="reveal" style={{ "--delay": "310ms" }}>
          The security number is the one that gets executive attention. IBM's{" "}
          <a
            href="https://newsroom.ibm.com/2025-07-30-ibm-report-13-of-organizations-reported-breaches-of-ai-models-or-applications,-97-of-which-reported-lacking-proper-ai-access-controls"
            target="_blank"
            rel="noopener noreferrer"
          >
            2025 Cost of a Data Breach Report
          </a>
          , conducted by Ponemon Institute across 600 breached organizations,
          found that 1 in 5 reported a breach due to shadow AI. Organizations
          with high levels of shadow AI saw an average of $670,000 in{" "}
          <em>additional</em> breach costs compared with those with little or
          none. Shadow AI incidents also skewed toward the most sensitive data:
          personally identifiable information was compromised in 65% of them
          versus a 53% global average, and intellectual property in 40% versus
          33%.
        </p>
        <p className="reveal" style={{ "--delay": "320ms" }}>
          The same report exposes why: 63% of breached organizations either had
          no AI governance policy or were still drafting one, only 37% had
          policies to manage AI or detect shadow AI at all, and among those that
          did have a policy, just 34% ran regular audits for unsanctioned AI. Of
          the organizations that reported a breach of an AI model or
          application, 97% lacked proper AI access controls.
        </p>
        <p className="reveal" style={{ "--delay": "330ms" }}>
          But the security cost is only the visible half. The operational cost
          is quieter and, for most teams, larger. Every shadow workflow is an
          undocumented process owned by one person. It cannot be reviewed,
          improved, delegated, or inherited. It is exactly the{" "}
          <Link to="/blog/automation-debt-workflow-sprawl">
            automation debt
          </Link>{" "}
          problem, except the debt is invisible on the balance sheet because
          nobody knows the workflow exists.
        </p>

        <div className="cmp-table-wrap reveal" style={{ "--delay": "340ms" }}>
          <table className="cmp-table">
            <thead>
              <tr>
                <th scope="col">Dimension</th>
                <th scope="col">Shadow path</th>
                <th scope="col">Governed path</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="cmp-cell-strong">Who knows it exists</td>
                <td>One person</td>
                <td>The team and its owner</td>
              </tr>
              <tr>
                <td className="cmp-cell-strong">Data boundary</td>
                <td>Crossed, unrecorded</td>
                <td>Enforced by policy and scope</td>
              </tr>
              <tr>
                <td className="cmp-cell-strong">Access model</td>
                <td>A personal login</td>
                <td>Least privilege, revocable</td>
              </tr>
              <tr>
                <td className="cmp-cell-strong">High-impact actions</td>
                <td>Whatever the tool allows</td>
                <td>Held for named approval</td>
              </tr>
              <tr>
                <td className="cmp-cell-strong">Record of what happened</td>
                <td>None</td>
                <td>Full audit trail</td>
              </tr>
              <tr>
                <td className="cmp-cell-strong">Survives a departure</td>
                <td>No</td>
                <td>Yes, it is a runbook</td>
              </tr>
              <tr>
                <td className="cmp-cell-strong">Compounds over time</td>
                <td>No, it stays a personal trick</td>
                <td>Yes, it becomes shared capability</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 id="blocking" className="reveal" style={{ "--delay": "360ms" }}>
          Why blocking AI tools does not work
        </h2>
        <p className="reveal" style={{ "--delay": "370ms" }}>
          The instinct after reading those numbers is to lock everything down.
          The evidence says restriction relocates the problem rather than
          solving it.
        </p>
        <p className="reveal" style={{ "--delay": "380ms" }}>
          UpGuard found that 41% of employees simply work around blocked
          applications. More awkwardly, it found an inverse relationship between
          training and compliance: employees who had received AI safety training
          and understood the risks were among the <em>most</em> frequent users
          of unapproved tools. Training produced capability, not caution. And
          PagerDuty found that among people who used AI they believed was
          disallowed, 53% received only informal guidance to stop, which is a
          fair description of a rule nobody expects to be enforced.
        </p>
        <p className="reveal" style={{ "--delay": "390ms" }}>
          There is a second-order cost too. A block pushes the activity onto
          personal devices and personal accounts, where your visibility drops to
          zero and your ability to log anything disappears entirely. You have
          not reduced the risk. You have made it unobservable, which is worse,
          because you can no longer size it.
        </p>

        <div className="callout info reveal" style={{ "--delay": "400ms" }}>
          <strong>The design principle:</strong> make the secure path the fast
          path. People route around friction, not around governance. Governance
          that costs nothing at the point of use gets followed.
        </div>

        <h2 id="two-paths" className="reveal" style={{ "--delay": "410ms" }}>
          Two paths for the same request
        </h2>
        <p className="reveal" style={{ "--delay": "420ms" }}>
          Consider one ordinary request: summarize this week's account risks and
          update the owners. Nothing about the demand changes between the two
          sides of the diagram below. What changes is where it runs and what it
          leaves behind.
        </p>

        <figure className="reveal" style={{ "--delay": "430ms" }}>
          <img
            src={pathsImg}
            alt="Two paths for the same request: the shadow path where an employee uses a personal AI account leaving no log and no reusable process, and the governed path where the same request runs through the BossDrop AI layer with scoped access, an approval gate, and an audit trail"
            style={{ width: "100%", height: "auto", borderRadius: 14 }}
            width="1040"
            height="560"
            loading="lazy"
          />
          <figcaption className="muted" style={{ marginTop: 8 }}>
            Same request, same day, same speed. One path produces a personal
            trick and an unrecorded data transfer. The other produces a reusable
            runbook and an audit trail.
          </figcaption>
        </figure>

        <p className="reveal" style={{ "--delay": "440ms" }}>
          This is why we describe shadow AI as a routing problem rather than a
          discipline problem. The employee on both sides is doing the same
          reasonable thing. Your job is not to change their motivation. It is to
          make sure the request lands somewhere you can see.
        </p>

        <h2 id="framework" className="reveal" style={{ "--delay": "450ms" }}>
          A five-step framework to bring shadow AI into the light
        </h2>
        <ol className="reveal numbered" style={{ "--delay": "460ms" }}>
          <li>
            <strong>Run an amnesty inventory, not a hunt.</strong> Ask every
            team what AI they already use and what it does for them, with an
            explicit no-penalty guarantee for anything disclosed in the window.
            You will learn more in two weeks than a monitoring project will
            surface in six months, because the people who know are the ones
            hiding it. Pair the responses with SaaS discovery data and expense
            reports for personal AI subscriptions, and write down the{" "}
            <em>job</em> each tool is doing, not just its name.
          </li>
          <li>
            <strong>Classify data before you classify tools.</strong> Most
            policies fail because they enumerate products, and the product list
            is obsolete in a quarter. Write the rule about the data instead:
            which classes of information may go to a public model, which may go
            only to a contracted environment, and which may never leave your
            boundary. That rule survives the next tool launch. The{" "}
            <a
              href="https://www.nist.gov/itl/ai-risk-management-framework"
              target="_blank"
              rel="noopener noreferrer"
            >
              NIST AI Risk Management Framework
            </a>{" "}
            is a reasonable free scaffold if you are starting from nothing.
          </li>
          <li>
            <strong>Ship a sanctioned path that is genuinely faster.</strong>{" "}
            This is the step that decides whether the other four matter. If the
            approved route takes a ticket and three days, shadow AI continues.
            The approved route needs to be available at the moment of need,
            connected to the systems the work already lives in, and at least as
            good at the task. Speed is the control.
          </li>
          <li>
            <strong>Put the gate on actions, not on thinking.</strong> Reading,
            drafting, and summarizing rarely need approval. Anything that
            changes the world outside the tool does: sending an external
            message, changing a commitment, moving money, granting access,
            writing to a system of record. Scope permissions to least privilege,
            default new agents to propose-only, and promote them to autonomous
            per action as they earn it. We covered the design of that ladder in{" "}
            <Link to="/blog/audit-ready-ai-agent-automation">
              audit-ready automation
            </Link>
            .
          </li>
          <li>
            <strong>Log everything and audit on a schedule.</strong> Every
            automated action should record its trigger, its inputs, what it
            changed, and who approved it. Then actually re-run the inventory
            quarterly. IBM's finding that, among organizations that do have an
            AI governance policy, only 34% run regular audits for unsanctioned
            AI is the reason so many policies are accurate on the day they are
            written and fiction six months later.
          </li>
        </ol>

        <div className="panel reveal" style={{ "--delay": "470ms" }}>
          <h3 className="h2">A short readiness checklist</h3>
          <ul className="bullets">
            <li>
              Can you name the top ten AI tools in use in your company this
              week?
            </li>
            <li>
              Does your policy tell people what they <em>can</em> do, or only
              what they cannot?
            </li>
            <li>
              Is there an approved way to get an AI task done today, without a
              ticket?
            </li>
            <li>
              For anything that can write to a real system, is access scoped and
              revocable?
            </li>
            <li>
              If an automated action caused a problem, could you reconstruct
              what ran and who approved it?
            </li>
            <li>When is the next audit, and who owns it?</li>
          </ul>
          <p className="muted">
            Three or more "no" answers means you do not have a shadow AI
            problem. You have a governance gap that shadow AI is filling.
          </p>
        </div>

        <h2 id="bossdrop" className="reveal" style={{ "--delay": "480ms" }}>
          What this looks like in BossDrop
        </h2>
        <p className="reveal" style={{ "--delay": "490ms" }}>
          BossDrop is built for step three and step four: a sanctioned path fast
          enough that nobody wants the shadow one, with the controls attached by
          default rather than bolted on later. It sits above the stack you
          already run rather than replacing it, so the governed path leads back
          into your own tools.
        </p>

        <div className="panel panelDark reveal" style={{ "--delay": "500ms" }}>
          <h3 className="h2">How the governed path is built</h3>
          <ul className="bullets">
            <li>
              <strong>Connected, not copied.</strong> BossDrop reads the real
              state of work from tasks, chat, calendars, and docs, so people
              stop pasting context into a personal chatbot to get an answer.
              See <Link to="/how-it-works">how it works</Link>.
            </li>
            <li>
              <strong>Least-privilege access.</strong> Every connection is
              scoped and revocable, and agents start read-only. No personal API
              keys, no side accounts.
            </li>
            <li>
              <strong>Propose-then-approve.</strong> Drafting and analysis run
              freely; anything irreversible waits for a named human. The
              accountability model is the one we described in{" "}
              <Link to="/blog/agentic-raci">Agentic RACI</Link>.
            </li>
            <li>
              <strong>Audit trail by default.</strong> What triggered an action,
              what it touched, what changed, and who approved it, recorded
              without anyone remembering to record it.
            </li>
            <li>
              <strong>Runbooks instead of personal tricks.</strong> A workflow
              one person invented becomes a shared, reviewable process that
              survives their next role change.
            </li>
            <li>
              <strong>Enterprise controls where you need them.</strong> Access
              boundaries, retention, and reporting for regulated teams, covered
              on the <Link to="/enterprise">enterprise page</Link>.
            </li>
          </ul>
          <p className="muted">
            Full capability list on <Link to="/features">features</Link>, plans
            on <Link to="/pricing">pricing</Link>, and how BossDrop differs from
            conventional work tools on{" "}
            <Link to="/compare">the comparison pages</Link>.
          </p>
        </div>

        <div className="cta-row reveal" style={{ "--delay": "510ms" }}>
          <a className="btn btnPrimary" {...DEMO_LINK}>
            See the governed path
          </a>
          <Link className="btn btnGhost" to="/teams">
            Explore BossDrop for teams
          </Link>
        </div>

        <h2 id="metrics" className="reveal" style={{ "--delay": "520ms" }}>
          Metrics that tell you it is working
        </h2>
        <p className="reveal" style={{ "--delay": "530ms" }}>
          Do not measure this in policy acknowledgments. Measure it in behavior.
        </p>
        <ul className="bullets reveal" style={{ "--delay": "540ms" }}>
          <li>
            <strong>Disclosed tool count, rising then flattening.</strong> A
            rising number early is success, not failure. It means people trust
            the amnesty.
          </li>
          <li>
            <strong>Share of AI work running on the sanctioned path.</strong>{" "}
            The single number that matters. If it is not climbing, your approved
            route is still slower.
          </li>
          <li>
            <strong>Time from "I need this automated" to a working
            workflow.</strong> This is the friction that creates shadow AI.
            Track it like a latency metric, the way we treat{" "}
            <Link to="/blog/decision-latency">decision latency</Link>.
          </li>
          <li>
            <strong>Percentage of automated actions with a complete audit
            record.</strong> Should be at or near 100%. Anything less is a blind
            spot with a name.
          </li>
          <li>
            <strong>Workflows with more than one owner.</strong> The antidote to
            a personal trick is a documented process someone else can run.
          </li>
        </ul>

        <h2 id="resources" className="reveal" style={{ "--delay": "550ms" }}>
          External resources worth reading
        </h2>
        <p className="reveal" style={{ "--delay": "560ms" }}>
          The primary research behind the numbers in this post:
        </p>
        <ul className="bullets reveal" style={{ "--delay": "570ms" }}>
          <li>
            <a
              href="https://www.pagerduty.com/newsroom/shadow-ai-workplace-survey-2026/"
              target="_blank"
              rel="noopener noreferrer"
            >
              PagerDuty Shadow AI Survey 2026: two-thirds of office
              professionals have used unauthorized AI at work
            </a>
          </li>
          <li>
            <a
              href="https://newsroom.ibm.com/2025-07-30-ibm-report-13-of-organizations-reported-breaches-of-ai-models-or-applications,-97-of-which-reported-lacking-proper-ai-access-controls"
              target="_blank"
              rel="noopener noreferrer"
            >
              IBM 2025 Cost of a Data Breach Report: shadow AI, governance gaps,
              and the $670,000 premium
            </a>
          </li>
          <li>
            <a
              href="https://www.upguard.com/press/new-research-from-upguard-reveals-68-of-security-leaders-admit-to-unauthorized-ai-usage"
              target="_blank"
              rel="noopener noreferrer"
            >
              UpGuard State of Shadow AI: 68% of security leaders admit to
              unauthorized AI usage
            </a>
          </li>
          <li>
            <a
              href="https://www.microsoft.com/en-us/worklab/work-trend-index/ai-at-work-is-here-now-comes-the-hard-part"
              target="_blank"
              rel="noopener noreferrer"
            >
              Microsoft and LinkedIn Work Trend Index: 78% of AI users bring
              their own AI tools to work
            </a>
          </li>
          <li>
            <a
              href="https://www.microsoft.com/en-us/worklab/work-trend-index/agents-human-agency-and-the-opportunity-for-every-organization"
              target="_blank"
              rel="noopener noreferrer"
            >
              Microsoft 2026 Work Trend Index: agents, human agency, and the
              shift to directing work
            </a>
          </li>
          <li>
            <a
              href="https://www.mckinsey.com/capabilities/quantumblack/our-insights/the-state-of-ai"
              target="_blank"
              rel="noopener noreferrer"
            >
              McKinsey, The state of AI in 2025: agents, innovation, and
              transformation
            </a>
          </li>
          <li>
            <a
              href="https://www.mckinsey.com/capabilities/tech-and-ai/our-insights/tech-forward/state-of-ai-trust-in-2026-shifting-to-the-agentic-era"
              target="_blank"
              rel="noopener noreferrer"
            >
              McKinsey, State of AI trust in 2026: governance maturity is
              lagging adoption
            </a>
          </li>
          <li>
            <a
              href="https://www.nist.gov/itl/ai-risk-management-framework"
              target="_blank"
              rel="noopener noreferrer"
            >
              NIST AI Risk Management Framework, a free starting scaffold for AI
              governance
            </a>
          </li>
        </ul>

        <div className="panel reveal" style={{ "--delay": "580ms" }}>
          <h3 className="h2">Related BossDrop posts</h3>
          <ul className="bullets">
            <li>
              <Link to="/blog/audit-ready-ai-agent-automation">
                Audit-Ready Automation: How to Use AI Agents Without Losing
                Control
              </Link>
            </li>
            <li>
              <Link to="/blog/agentic-raci">
                Agentic RACI: Redefining Responsibility When AI Does the Doing
              </Link>
            </li>
            <li>
              <Link to="/blog/context-switching-tax">
                The Context-Switching Tax: What Fragmented Attention Costs Your
                Team
              </Link>
            </li>
            <li>
              <Link to="/blog/span-of-control-agentic-ai">
                Span of Control: Why Middle Management Caps Your Team
              </Link>
            </li>
          </ul>
          <p className="muted">
            Shadow AI is the demand signal. Audit-ready automation is the
            supply.
          </p>
        </div>

        <div id="faq">
        <FAQ
          reveal={false}
          items={[
            {
              q: "What is shadow AI?",
              a: "Shadow AI is any use of AI tools, models, or agents for work that happens outside your organization's approval, visibility, and controls. It is the AI-era version of shadow IT: employees adopt a tool on their own because it solves a real problem faster than the sanctioned path does. It appears as shadow input (company data sent to a personal account), shadow output (AI-produced work entering decisions with no provenance), and shadow action (personal keys and extensions that read from or write to real systems).",
            },
            {
              q: "How common is shadow AI in the workplace?",
              a: "PagerDuty's 2026 survey of 1,250 office professionals at companies with $500 million or more in revenue found 66% had used AI at work despite believing it was against policy, rising to 72% at organizations with 1,500 or more employees. UpGuard puts unauthorized AI use at roughly 8 in 10 employees, and IBM's 2025 Cost of a Data Breach Report found 1 in 5 organizations reported a breach tied to shadow AI.",
            },
            {
              q: "Why do employees use unapproved AI tools?",
              a: "Speed and familiarity, almost never malice. Microsoft and LinkedIn's Work Trend Index found 78% of AI users bring their own tools to work and only 39% had received AI training from their employer. PagerDuty found 89% first encountered their AI tool in their personal life. When someone already knows a tool works and no approved equivalent exists, the unapproved path is simply the shorter one.",
            },
            {
              q: "Does blocking AI tools stop shadow AI?",
              a: "Rarely. UpGuard found 41% of employees work around blocked applications, and that employees who had received AI safety training used unapproved tools most frequently. Blocking pushes the activity onto personal devices and accounts where you have no visibility and no logs at all, which makes the risk unobservable rather than smaller. Providing a faster governed path outperforms restricting the ungoverned one.",
            },
            {
              q: "How do you detect shadow AI in your organization?",
              a: "Start with an amnesty inventory rather than a monitoring sweep: ask teams what they use, with no penalty for disclosure. Combine that with SaaS and network discovery data, expense reports showing personal AI subscriptions, and a recurring audit. IBM found that among organizations that do have an AI governance policy, only 34% audit for unsanctioned AI, which is why most tool inventories are out of date within a quarter.",
            },
            {
              q: "What should an AI governance policy include?",
              a: "A data classification rule covering what may be sent to which class of tool; a short list of approved tools with a genuinely fast path to add more; least-privilege, revocable access for anything that can act; an approval gate on irreversible actions; and an audit trail recording what ran, on what, and who approved it. A policy that lists only prohibitions tends to produce shadow AI rather than prevent it.",
            },
          ]}
        />
        </div>

        <div className="panel reveal" style={{ "--delay": "640ms" }}>
          <div className="kpis">
            <span className="pill">shadow ai</span>
            <span className="pill">ai governance</span>
            <span className="pill">audit trail</span>
            <span className="pill">agentic automation</span>
          </div>
          <p className="muted">
            Your people have already told you what they want automated. They
            just told you by doing it in the dark. Give the work a{" "}
            <strong>path you can see</strong>, and{" "}
            <a {...DEMO_LINK}>book a demo</a> or{" "}
            <Link to="/contact-sales">talk to sales</Link> when you are ready to
            build it.
          </p>
        </div>
      </article>
    </section>
  );
}

/* ---------- helpers ---------- */
function setMeta(content) {
  setOrCreate(
    'meta[name="description"]',
    (el) => el.setAttribute("name", "description"),
    content,
  );
}
function setKeywords(content) {
  setOrCreate(
    'meta[name="keywords"]',
    (el) => el.setAttribute("name", "keywords"),
    content,
  );
}
function setOG(property, content) {
  if (!content) return;
  const sel = property.startsWith("og:")
    ? `meta[property="${property}"]`
    : `meta[name="${property}"]`;
  setOrCreate(
    sel,
    (el) => {
      property.startsWith("og:")
        ? el.setAttribute("property", property)
        : el.setAttribute("name", property);
    },
    content,
  );
}
function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}
function setOrCreate(selector, init, content) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    init(el);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function injectLDJSON(id, obj) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}
function toAbs(url) {
  if (!url) return "";
  try {
    return new URL(url, window.location.origin).toString();
  } catch {
    return url;
  }
}

/* --- inline SVGs --- */
function iconX() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M18.9 2H22l-7.7 8.8L22 22h-6.3l-4.9-6-5.6 6H2l8.5-9.4L2 2h6.4l4.4 5.4L18.9 2z"
      />
    </svg>
  );
}
function iconLinkedIn() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM0 8h5v16H0V8zm7.5 0h4.8v2.2h.1c.7-1.2 2.3-2.5 4.7-2.5 5 0 5.9 3.3 5.9 7.6V24h-5V16.4c0-1.8 0-4.1-2.5-4.1s-2.9 2-2.9 4v7.7h-5V8z"
      />
    </svg>
  );
}
function iconFacebook() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.7-3.9 1.1 0 2.2.2 2.2.2v2.4h-1.2c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12"
      />
    </svg>
  );
}
